import React, { useState } from "react";
import type { SxProps, Theme } from "@mui/material/styles";
import type { SystemStyleObject } from "@mui/system";
import {
    Accordion as MuiAccordion,
    AccordionSummary as MuiAccordionSummary,
    AccordionDetails as MuiAccordionDetails,
} from "@mui/material";
import { Icon } from "@/components/Icon";

export interface AccordionProps {
    title: React.ReactNode;
    subtitle?: React.ReactNode;
    icon?: string | React.ReactNode;
    badge?: React.ReactNode;
    actions?: React.ReactNode;
    children: React.ReactNode;
    defaultExpanded?: boolean;
    expanded?: boolean;
    onChange?: (expanded: boolean) => void;
    sx?: SxProps<Theme>;
    sxSummary?: SxProps<Theme>;
    className?: string;
    headerClassName?: string;
    contentClassName?: string;
    variant?: "default" | "active" | "bordered";
    expandicon?: React.ReactNode;
    rounded?: boolean;
    elevation?: boolean;
    slide?: boolean;
    floatingLabel?: React.ReactNode;
    hoverGreen?: boolean;
    centerTitle?: boolean;
}

export const Accordion: React.FC<AccordionProps> = ({
    title,
    subtitle,
    icon,
    badge,
    actions,
    children,
    defaultExpanded = true,
    expanded: controlledExpanded,
    onChange,
    sx = {},
    sxSummary = {},
    className = "",
    headerClassName = "",
    contentClassName = "",
    variant = "default",
    expandicon = <Icon value="chevronDown" size={16} />,
    rounded = true,
    elevation = false,
    slide = false,
    floatingLabel,
    hoverGreen = false,
    centerTitle = false,
}) => {
    const isControlled = controlledExpanded !== undefined;
    const [internalExpanded, setInternalExpanded] =
        useState<boolean>(defaultExpanded);

    const isExpanded = isControlled ? controlledExpanded : internalExpanded;
    const summaryContentStyle = (
        sxSummary as Record<string, SystemStyleObject<Theme>>
    )["& .MuiAccordionSummary-content"];

    const handleToggle = (
        _event: React.SyntheticEvent,
        newExpanded: boolean
    ): void => {
        if (!isControlled) {
            setInternalExpanded(newExpanded);
        }
        if (onChange) {
            onChange(newExpanded);
        }
    };

    const variantBorderClasses =
        variant === "active"
            ? hoverGreen
                ? "border-2 border-emerald-400 dark:border-emerald-600"
                : "border-2 border-primary/40 dark:border-primary/50"
            : "border border-slate-200 dark:border-zinc-800";

    return (
        <div className={`relative ${className}`}>
            {floatingLabel && (
                <span className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
                    {floatingLabel}
                </span>
            )}
            <MuiAccordion
                expanded={isExpanded}
                onChange={handleToggle}
                elevation={elevation ? 1 : 0}
                disableGutters={!slide}
                sx={{
                    ...(rounded && {
                        borderRadius: "15px !important",
                        overflow: "hidden",
                    }),
                    "&:before": { display: "none" },
                    "&.Mui-expanded": { margin: "0 !important" },
                    ...sx,
                }}
                className={`${variantBorderClasses} transition-colors duration-200`}
            >
                <MuiAccordionSummary
                    expandIcon={expandicon ? expandicon : null}
                    className={`group/summary transition-colors duration-200 ${rounded ? (isExpanded ? "rounded-t-[15px]!" : "rounded-[15px]!") : ""} ${headerClassName}`}
                    sx={{
                        minHeight: "unset !important",
                        "&.Mui-expanded": { minHeight: "unset !important" },
                        "& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded":
                            { margin: "10px 0 !important" },
                        "& .MuiAccordionSummary-expandIconWrapper": {
                            color: "inherit",
                        },
                        ...sxSummary,
                        ...(summaryContentStyle && {
                            "& .MuiAccordionSummary-content.Mui-expanded":
                                summaryContentStyle,
                        }),
                    }}
                >
                    <div
                        className={`${expandicon ? "pr-2" : ""} relative flex flex-col sm:flex-row sm:items-center justify-between gap-3 w-full`}
                    >
                        <div
                            className={`flex items-center gap-2 min-w-0 ${centerTitle ? "w-full justify-center text-center" : ""}`}
                        >
                            {icon && (
                                <span className="shrink-0 flex items-center">
                                    {typeof icon === "string" ? (
                                        <Icon
                                            value={icon}
                                            size={18}
                                            className="text-primary"
                                        />
                                    ) : (
                                        icon
                                    )}
                                </span>
                            )}

                            <div className="font-bold text-inherit text-sm flex items-center gap-2 min-w-0">
                                {title}
                            </div>
                        </div>

                        {subtitle && (
                            <div className="flex items-center justify-center sm:absolute sm:left-1/2 sm:-translate-x-1/2 pointer-events-none">
                                <div className="pointer-events-auto">
                                    {subtitle}
                                </div>
                            </div>
                        )}

                        {(badge || actions) && (
                            <div className="flex items-center gap-2 shrink-0 self-end sm:self-center ml-auto">
                                {badge && (
                                    <div className="shrink-0">{badge}</div>
                                )}

                                {actions && (
                                    <div
                                        className="flex items-center gap-2 shrink-0"
                                        onClick={(e) => e.stopPropagation()}
                                        onPointerDown={(e) =>
                                            e.stopPropagation()
                                        }
                                    >
                                        {actions}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </MuiAccordionSummary>

                <MuiAccordionDetails
                    sx={{
                        padding: 0,
                        backgroundColor: "transparent",
                    }}
                    className={contentClassName}
                >
                    {children}
                </MuiAccordionDetails>
            </MuiAccordion>
        </div>
    );
};
